import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CheckCircle2, CircleDashed, Loader2, Play, XCircle } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";

import type { Job, JobRunRow, JobTrigger, ManualRunStatus } from "../types/job";
import { liveViewOf, scheduleText } from "../utils/pending";
import { JobRunSheet } from "./JobRunSheet";

interface JobPendingIndicatorProps {
  job: Job;
  productId: string;
  realtime: boolean;
  canWrite: boolean;
  onRun: (job: Job) => void;
  onPublish: (job: Job) => void;
  onOpenSettings?: (jobId: string) => void;
}

/**
 * Ao lado do selo de publicação, só quando o rascunho tem algo por publicar
 * (AB#87442). A forma e a cor dizem como foi a execução manual desta versão — a
 * forma muda junto, para não depender só da cor —, e o clique abre o resumo: o
 * que mudou, a execução e as ações de executar e publicar.
 */
export function JobPendingIndicator({
  job,
  productId,
  realtime,
  canWrite,
  onRun,
  onPublish,
  onOpenSettings,
}: JobPendingIndicatorProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.split("-")[0];
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<JobRunRow | null>(null);
  const pending = job.pending;
  if (!pending) return null;

  const run = pending.manual_run;
  const label = t(`jobs.pending.state.${run.status}`);
  const live = job.live ? liveViewOf(job) : null;
  const triggerText = (trigger: JobTrigger, schedule: Job["schedule_description"]) =>
    trigger.type === "webhook" ? t("agents.trigger.types.webhook") : scheduleText({ trigger, schedule_description: schedule }, locale);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={label}
            aria-label={label}
          >
            <StateIcon status={run.status} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-3 text-sm">
          <p className="font-medium">{t(job.published ? "jobs.pending.titlePublished" : "jobs.pending.titleDraft")}</p>
          {!job.published && <p className="text-muted-foreground">{t("jobs.pending.neverPublished")}</p>}

          {pending.changes.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("jobs.pending.changed")}</p>
              <ul className="space-y-1.5">
                {pending.changes.map((field) => (
                  <li key={field}>
                    <span className="font-medium">{t(`jobs.pending.fields.${field}`)}</span>
                    {field === "trigger" && live && (
                      <p className="text-muted-foreground">
                        {t("jobs.pending.live")}: {triggerText(live.trigger, live.schedule_description)}
                        <br />
                        {t("jobs.pending.draft")}: {triggerText(job.trigger, job.schedule_description)}
                      </p>
                    )}
                    {field === "instruction" && (
                      <p className="line-clamp-3 text-muted-foreground" title={job.instruction}>
                        {job.instruction}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("jobs.pending.lastManualRun")}
            </p>
            {run.status === "not_run" ? (
              <p className="text-muted-foreground">{t("jobs.pending.notRunYet")}</p>
            ) : (
              <div className="flex items-center gap-2">
                <StateIcon status={run.status} />
                <span>{label}</span>
                {run.at && (
                  <span className="text-muted-foreground">
                    ·{" "}
                    {formatDistanceToNow(new Date(run.at), { addSuffix: true, locale: locale === "pt" ? ptBR : undefined })}
                  </span>
                )}
              </div>
            )}
            {run.execution_id && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={() => {
                  setOpen(false);
                  setResult(resultRow(job, run.execution_id!, run.status, run.at));
                }}
              >
                {t("jobs.pending.viewResult")}
              </Button>
            )}
          </div>

          {canWrite && (
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  onRun(job);
                }}
              >
                <Play className="mr-1.5 h-3.5 w-3.5" />
                {t("jobs.actions.test")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setOpen(false);
                  onPublish(job);
                }}
              >
                {t("jobs.actions.publish")}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <JobRunSheet
        productId={productId}
        row={result}
        realtime={realtime}
        onClose={() => setResult(null)}
        onOpenSettings={onOpenSettings}
      />
    </>
  );
}

function StateIcon({ status }: { status: ManualRunStatus }) {
  const className = "h-4 w-4 shrink-0";
  if (status === "completed") return <CheckCircle2 className={`${className} text-green-600 dark:text-green-500`} />;
  if (status === "failed") return <XCircle className={`${className} text-destructive`} />;
  if (status === "running") return <Loader2 className={`${className} animate-spin text-muted-foreground`} />;
  return <CircleDashed className={`${className} text-muted-foreground`} />;
}

/** A linha que o painel de execução lê; o resto ele busca pelo id. */
function resultRow(job: Job, executionId: string, status: ManualRunStatus, at: string | null): JobRunRow {
  return {
    execution_id: executionId,
    job: { id: job.id, name: job.name },
    status: status === "completed" ? "completed" : status === "failed" ? "failed" : "running",
    trigger_type: "manual",
    started_at: at,
    completed_at: null,
    duration_ms: null,
    summary: null,
    agent_status: null,
    writes: 0,
    pending_count: 0,
    error: null,
  };
}
